import React, { useCallback, useMemo, useState } from "react";
import { BaseModal, IModalInstance, ModalHeader, modalService } from "@whatslly/ui-components/modals";
import { useTranslation } from "react-i18next";
import styles from "./ImportUsersModal.module.scss";
import { FileDropzone } from "@whatslly/ui-components/v2/FileDropzone";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { Button } from "@whatslly/ui-components/v2/Button";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { ISidecarsStore } from "../../pages/Sidecars/ISidecarsStore";
import { InjectSidecarsStore } from "../../pages/Sidecars/ISidecarsStore";
import { IUserManagementDataLayer, UserManagementDataLayerSymbol } from "@whatsllyorg/crm-components/UserManagement";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { arrayToCsv, parseCSV, CSV_UTF8_BOM } from "@whatslly/commons/csvUtils";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import classnames from "classnames";
import { FaTrash } from "react-icons/fa";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { Text } from "@whatslly/ui-components/Text";
import { trimObjectStrings, calFileSizeV2 } from "@whatslly/commons/utils";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { Table } from "@whatslly/ui-components/v2/Tables/Table/Table";
import isEmail from "validator/es/lib/isEmail";
import ms from "ms";
import {
  InvitePlatformSwitch,
  InviteDesktopExtensionsSwitch,
  NotifyInviterOnSidecarEventsSwitch,
  UpdateExistingUsersSwitch,
  InviteRemoteSyncSwitch,
} from "@whatsllyorg/crm-components/UserManagement";
import { observer } from "mobx-react-lite";
import { UserDefaultChannels } from "@whatslly/types/User";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { SidecarChannelType } from "@whatslly/types/sidecars-server/Sidecar";
import type { PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";

export const ImportUsersModal: React.FC<
  IModalInstance<{
    csvContent: { header: string[]; data: Record<string, string>[] } | undefined;
    inviteToPlatform: boolean;
    inviteToDesktopAndExtensions: boolean;
    notifyInviterOnSidecarEvents: boolean;
    updateExisting: boolean;
    defaultUserChannels: UserDefaultChannels | undefined;
    defaultUserPermission: string | undefined;
    inviteToRemoteSync: boolean;
    defaultUserRemoteSyncChannels: Record<SidecarChannelType, boolean> | undefined;
  }> & {
    instanceConfigs?: PermissionInstanceConfigs;
  }
> = observer(({ closeModal, instanceConfigs }) => {
  const { t } = useTranslation();
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const dataLayer = useDependencyInjector<IUserManagementDataLayer>(UserManagementDataLayerSymbol);
  const [csvContent, setCsvContent] = useState<{ header: string[]; data: Record<string, string>[] }>();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [defaultUserPermission, setDefaultUserPermission] = useState<string | undefined>(undefined);
  const [inviteToPlatform, setInviteToPlatform] = useState(false);
  const [inviteToDesktopAndExtensions, setInviteToDesktopAndExtensions] = useState(false);
  const [defaultUserChannels, setDefaultUserChannels] = useState<UserDefaultChannels | undefined>(undefined);
  const [defaultUserRemoteSyncChannels, setDefaultUserRemoteSyncChannels] = useState<Record<SidecarChannelType, boolean> | undefined>(undefined);
  const [notifyInviterOnSidecarEvents, setNotifyInviterOnSidecarEvents] = useState(true);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [inviteToRemoteSync, setInviteToRemoteSync] = useState(false);
  const selectedOrgConfigs = dataLayer.orgConfigs?.state === "fulfilled" ? dataLayer.orgConfigs.value : undefined;
  const orgCustomFields = dataLayer.orgConfigs?.case({
    fulfilled: (orgConfigs) => orgConfigs.configs?.custom_user_fields ?? [],
  });

  const cols = useMemo(() => {
    const items = [
      {
        name: "Email",
        required: true,
      },
      {
        name: "Username",
        required: false,
      },
      {
        name: "First Name",
        required: true,
      },
      {
        name: "Last Name",
        required: true,
      },
    ];
    if (sidecarsStore.canCreateSidecar) {
      items.push({
        name: "Remote Sync Phone",
        required: false,
      });
    }
    if (dataLayer.casAssignGroup) {
      items.push({
        name: "Group",
        required: false,
      });
    }
    items.push({
      name: "Manager",
      required: false,
    });
    items.push({
      name: "Phones",
      required: false,
    });
    if (Array.isArray(orgCustomFields) && orgCustomFields.length) {
      orgCustomFields.forEach((fieldName) =>
        items.push({
          name: fieldName.normalize("NFC"),
          required: false,
        })
      );
    }
    return items;
  }, [dataLayer.casAssignGroup, sidecarsStore.canCreateSidecar, orgCustomFields]);

  const colsNames = useMemo(() => cols.map((item) => item.name), [cols]);

  const colsReqNames = useMemo(() => cols.filter((item) => item.required).map((item) => item.name), [cols]);

  const downloadCsvTemplate = useCallback(() => {
    downloadBlob(`${CSV_UTF8_BOM}${arrayToCsv([[...colsNames]])}`, `tuvis-csv-template.csv`, `text/csv;charset=utf-8;`);
  }, [colsNames]);

  const onFileSelect = useCallback(
    (csvContent: string, file: File) => {
      const { data, header } = parseCSV(csvContent);
      const invalidHeaders = header.filter((item) => !colsNames.includes(item));
      const missingRequiredHeaders = colsReqNames.filter((item) => !header.includes(item));
      if (data.length === 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.empty_file"),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      if (missingRequiredHeaders.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_required_headers", { missingRequiredHeaders }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      if (invalidHeaders.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.invalid_headers", { invalidHeaders }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      const trimmedData = trimObjectStrings(data);
      const invalidEmails = trimmedData.filter((item) => !item.Email || !isEmail(item.Email)).filter(Boolean);

      if (invalidEmails.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.invalid_emails", { invalidEmails: invalidEmails.map((item) => item.Email) }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      const missingFirstName = trimmedData.filter((item) => !item["First Name"]);

      if (missingFirstName.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_first_name", { missingFirstName: missingFirstName.map((item) => item.Email) }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      const missingLastName = trimmedData.filter((item) => !item["Last Name"]);

      if (missingLastName.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_last_name", { missingLastName: missingLastName.map((item) => item.Email) }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      setCsvContent({ data: trimmedData, header });
      setSelectedFile(file);
    },
    [colsNames, colsReqNames, t]
  );

  const previewCols = useMemo(() => {
    return cols.map((col) => {
      return {
        keyInsideItem: col.name,
        title: col.name,
      };
    });
  }, [cols]);

  const hasAnyUserType = useMemo(
    () =>
      updateExisting ||
      inviteToPlatform ||
      (inviteToDesktopAndExtensions && Object.values(defaultUserChannels ?? {}).some((value) => value)) ||
      (inviteToRemoteSync && Object.values(defaultUserRemoteSyncChannels ?? {}).some((value) => value)),
    [defaultUserChannels, defaultUserRemoteSyncChannels, inviteToDesktopAndExtensions, inviteToPlatform, inviteToRemoteSync, updateExisting]
  );

  return (
    <BaseModal
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={closeModal}
      className={styles.importUsersModal}
      bodyClassName={styles.importUsersModalBody}
      header={<ModalHeader closeModal={() => closeModal()}>{t("user_management.import_users_csv_button")}</ModalHeader>}
      footer={
        <ModalButtons
          isSubmitDisabled={!hasAnyUserType || !csvContent}
          submitText={t("user_management.import_button")}
          testId={`modal-import-button`}
          onCancel={() => closeModal()}
          onSubmit={() =>
            closeModal({
              csvContent,
              inviteToPlatform,
              inviteToDesktopAndExtensions,
              notifyInviterOnSidecarEvents,
              updateExisting,
              defaultUserChannels,
              defaultUserPermission,
              inviteToRemoteSync,
              defaultUserRemoteSyncChannels,
            })
          }
        />
      }
    >
      <div>{t("user_management.import_users_modal_text")}</div>
      <ul>
        {cols.map((col) => {
          return <li key={col.name}>{`${col.name}${col.required ? ` (${t("user_management.required")})` : ""}`}</li>;
        })}
      </ul>
      <div className={styles.controls}>
        <div>
          <Button
            secondary
            data-testid={`download-csv-template-button`}
            onClick={downloadCsvTemplate}
            paddingInline="small"
            text={t("user_management.import_users_csv_template")}
          />
        </div>
        <div className={styles.switchesContainer}>
          {sidecarsStore.canCreateSidecar && (
            <NotifyInviterOnSidecarEventsSwitch value={notifyInviterOnSidecarEvents} onChange={setNotifyInviterOnSidecarEvents} />
          )}
          <UpdateExistingUsersSwitch value={updateExisting} onChange={setUpdateExisting} />
          <InviteDesktopExtensionsSwitch
            orgConfigs={selectedOrgConfigs?.configs}
            updateDefaultChannels={(channel, newStatus) =>
              setDefaultUserChannels((defaultChannels) => ({ ...(defaultChannels || {}), [channel]: newStatus }))
            }
            value={inviteToDesktopAndExtensions}
            onChange={setInviteToDesktopAndExtensions}
          />
          {sidecarsStore.canCreateSidecar && sidecarsStore.availableChannelsForCreation?.length > 0 && (
            <InviteRemoteSyncSwitch
              availableChannelsForCreation={sidecarsStore.availableChannelsForCreation}
              value={inviteToRemoteSync}
              onChange={setInviteToRemoteSync}
              updateRemoteSyncChannels={(channel, newStatus) =>
                setDefaultUserRemoteSyncChannels((defaultChannels) => ({
                  ...(defaultChannels || { telegram: false, whatsapp: false, line: false, signal: false }),
                  [channel]: newStatus,
                }))
              }
            />
          )}
          <InvitePlatformSwitch
            instanceConfigs={instanceConfigs}
            value={inviteToPlatform}
            onChange={setInviteToPlatform}
            user={userStore.user}
            setPermissions={setDefaultUserPermission}
          />
        </div>
      </div>
      {selectedFile && csvContent ? (
        <div>
          <div
            className={classnames(
              styles.clearAndText,
              globalStyles["bg-color-secondary-primary"],
              globalStyles["bc-neutral-secondary"],
              globalStyles["color-main-primary"]
            )}
          >
            <div className={styles.textContainer}>
              <div className={styles.fileNameText}>{selectedFile.name}</div>
              <p className={classnames(styles.sizeText, globalStyles["color-neutral-primary"])}>({calFileSizeV2(selectedFile)})</p>
            </div>
            <div
              className={styles.clearIconContainer}
              onClick={() => {
                setCsvContent(undefined);
                setSelectedFile(undefined);
              }}
            >
              <Text className={classnames(styles.clearIcon, globalStyles["color-main-primary"])}>
                <FaTrash width="14" height="14"></FaTrash>
              </Text>
            </div>
          </div>
          <div className={styles.previewTableContainer}>
            <div>{t("user_management.import_users_preview")}</div>
            <Table className={styles.previewTable} items={csvContent?.data} emptyStateNode={<></>} columns={previewCols} />
          </div>
        </div>
      ) : (
        <FileDropzone accept=".csv" mode="string" className={styles.dropzone} onFile={onFileSelect}>
          <SvgV2 iconName="upload" height={25} />
          <div>{t("user_management.drop_csv")}</div>
        </FileDropzone>
      )}
    </BaseModal>
  );
});
